import type { WidgetTableProps } from '@/components/WidgetTable/types';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';

export type AIToolUser = {
  userId: string;
  email: string | null;
  name: string | null;
  machineId: string;
  endpointName?: string | null;
  timestamp: string;
};

/**
 * KIR-5281: rows are one per user per endpoint, so the endpoint is shown —
 * otherwise a user on two machines reads as a duplicated row. Header follows
 * the KIR-3035 endpoint_identifier rename (names are nulled server-side off).
 */
export function buildAiToolUserColumns(
  endpointEnabled: boolean
): WidgetTableProps<AIToolUser>['columns'] {
  return [
    {
      header: 'User',
      id: 'user',
      cell: user => {
        const displayName = user.name || user.email || user.userId;
        return (
          <span
            className="font-medium truncate block max-w-[200px]"
            title={displayName}
          >
            {displayName}
          </span>
        );
      },
      className: 'min-w-[150px]',
    },
    {
      header: 'Email',
      id: 'email',
      cell: user => (
        <span className="text-sm text-muted-foreground">
          {user.email || '—'}
        </span>
      ),
      className: 'min-w-[180px]',
    },
    {
      header: endpointEnabled ? 'Endpoint' : 'Machine',
      id: 'endpoint',
      cell: user => {
        const label = user.endpointName || user.machineId;
        return (
          <span
            className="text-sm font-mono truncate block max-w-[180px]"
            title={label}
          >
            {label}
          </span>
        );
      },
      className: 'min-w-[140px]',
    },
    {
      header: 'Last Seen',
      id: 'lastSeen',
      cell: user => <RelativeTimeWithTooltip date={user.timestamp} />,
      className: 'min-w-[120px]',
    },
  ];
}
